<template>
    <div v-if="isLoaded" class="page-wrap ep">
        <editor-bar :name="node.Name" :version="node.Version" :dirty="dirty" type-label="checklist" @cancel="cancel()" @save="openSave()" @height="barHeight = $event">
            <button type="button" class="ai-btn pb-btn" title="Improve with AI" @click="showAIModal = true">
                <svg viewBox="0 0 24 24" class="ep-fill"><path d="M13 0L3.5 14H10l-1.5 10L20 9h-6.5L15 0h-2z"/></svg><span>Improve with AI</span>
            </button>
        </editor-bar>

        <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
            <div class="ep-layout">
                <div class="ep-main">
                    <div class="ep-fmt" :class="{ live: activeEditor }" :style="{ top: barHeight + 'px' }">
                        <span class="ep-fmt-where">{{ activeEditor ? 'Note' : 'Click into a note to format it' }}</span>
                        <div v-if="activeEditor" class="ep-fmt-btns" @mousedown="keepEditorFocus">
                            <tip-tap-toolbar :editor="activeEditor" :show-align="true" />
                        </div>
                    </div>

                    <article class="ep-paper">
                        <editor-doc-head :locs="locs" :deps="deps" :types="types" :owner-names="ownerNames" :version="node.Version" @open="showDetails = true" />

                        <section v-for="list in lists" :key="list.key" class="cl-sec">
                            <div class="cl-sec-h">
                                <h3>
                                    <svg v-if="list.key == 'items'" viewBox="0 0 24 24"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
                                    <svg v-else viewBox="0 0 24 24"><path d="M4 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zM16 3v4M8 3v4M4 11h16M8 15h2v2H8z" /></svg>
                                    {{ list.title }} <span>{{ taskCount(list.key) }} task{{ taskCount(list.key) == 1 ? '' : 's' }}</span>
                                </h3>
                                <p>{{ list.hint }}</p>
                            </div>

                            <div v-for="group in groupsOf(list.key)" :key="String(group.key)" :id="'cl-group-' + list.key + '-' + group.key" class="cl-group">
                                <div class="cl-gh">{{ group.label }}<span>{{ group.rows.filter(r => r.item.type != 'textblock').length }}</span></div>
                                <div v-for="(row, k) in group.rows" :key="row.item.id" :class="['cl-row', { 'cl-note': row.item.type == 'textblock' }]">
                                    <svg v-if="row.item.type == 'textblock'" class="cl-ic" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v4h1" /></svg>
                                    <svg v-else class="cl-ic" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="3" /></svg>
                                    <div v-if="row.item.type == 'textblock'" class="cl-main cl-note-body">
                                        <tip-tap v-model="row.item.text" :hide-toolbar="true" :show-align="true" @focus="activeEditor = $event" />
                                    </div>
                                    <div v-else class="cl-main">
                                        <input v-model="row.item.label" :data-cl="row.item.id" class="cl-title" placeholder="Task" />
                                        <input v-model="row.item.description" class="cl-desc" placeholder="Details (optional)" />
                                    </div>
                                    <span class="cl-tools">
                                        <select class="cl-move" :value="groupKeyOf(list.key, row.item)" :aria-label="'Move to'" @change="moveToGroup(list.key, row.index, ($event.target as HTMLSelectElement).value)">
                                            <option v-for="g in moveOptions(list.key, row.item)" :key="String(g.key)" :value="String(g.key)">{{ g.label }}</option>
                                        </select>
                                        <button type="button" class="rs-icon" aria-label="Move up" :disabled="k == 0" @click="moveWithin(list.key, group, k, -1)"><svg viewBox="0 0 24 24"><path d="M6 15l6-6 6 6" /></svg></button>
                                        <button type="button" class="rs-icon" aria-label="Move down" :disabled="k == group.rows.length - 1" @click="moveWithin(list.key, group, k, 1)"><svg viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" /></svg></button>
                                        <button type="button" class="rs-icon" title="Duplicate" aria-label="Duplicate" @click="duplicate(list.key, row.index)"><svg viewBox="0 0 24 24"><path d="M9 9h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H11a2 2 0 0 1-2-2V9zM5 15H4a1 1 0 0 1-1-1V5a2 2 0 0 1 2-2h9a1 1 0 0 1 1 1v1" /></svg></button>
                                        <button type="button" class="rs-icon" :title="copiedID == row.item.id ? 'Copied' : 'Copy (paste into any checklist)'" aria-label="Copy" @click="copyItem(row.item)">
                                            <svg v-if="copiedID == row.item.id" viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                                            <svg v-else viewBox="0 0 24 24"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 3h6v4H9z" /></svg>
                                        </button>
                                        <button type="button" class="rs-icon del" aria-label="Delete" @click="remove(list.key, row.index)"><svg viewBox="0 0 24 24"><path d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4h6v3" /></svg></button>
                                    </span>
                                </div>
                                <div class="cl-adds">
                                    <button v-if="group.key !== NO_DAY" type="button" @click="addTask(list.key, group.key)"><svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>Add task</button>
                                    <button type="button" @click="addNote(list.key, group.key)"><svg viewBox="0 0 24 24"><path d="M5 4h14v16H5zM9 8h6M9 12h6M9 16h3" /></svg>Add note</button>
                                    <button type="button" @click="pasteItem(list.key, group.key)"><svg viewBox="0 0 24 24"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 3h6v4H9zM9 12h6M9 16h4" /></svg>Paste</button>
                                </div>
                            </div>
                            <p v-if="!groupsOf(list.key).length" class="cl-empty">{{ list.empty }}</p>
                            <div v-if="unusedGroups(list.key).length" class="cl-start">
                                <span>{{ list.key == 'items' ? 'Add tasks for' : 'Add a task on' }}</span>
                                <button v-for="g in unusedGroups(list.key)" :key="String(g.key)" type="button" class="ep-chip" @click="addTask(list.key, g.key)"><svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>{{ g.label }}</button>
                            </div>
                        </section>
                    </article>
                </div>

                <aside class="ep-side" aria-label="Outline" :style="{ top: barHeight + 'px' }">
                    <template v-for="list in lists" :key="list.key">
                        <div class="ep-side-h" :style="list.key == 'weeklyItems' ? 'margin-top:14px' : ''">{{ list.title }}</div>
                        <button v-for="group in groupsOf(list.key)" :key="String(group.key)" type="button" class="ep-ol" @click="jumpTo(list.key, group.key)">
                            <span>{{ group.rows.filter(r => r.item.type != 'textblock').length }}</span>{{ group.label }}
                        </button>
                    </template>
                    <editor-activity :comments="comments" :signed-count="signedCount" :required-count="requiredSigners.length" :version="node.Version" />
                </aside>
            </div>
        </div>

        <editor-details-drawer v-if="showDetails" v-model:name="node.Name" v-model:ownerIds="ownerIDs" v-model:locs="locs" v-model:deps="deps" v-model:types="types" type-label="checklist" @close="showDetails = false" />
        <editor-save-dialog v-if="showSave" v-model:status="node.NeedsApprovalType" :version="node.Version" :required-count="requiredSigners.length" :signed-count="signedCount" :busy="disableButton" note-placeholder="e.g. Added end-of-shift inventory counts" @cancel="showSave = false" @save="submitCheck($event)" />
        <editor-discard-dialog v-if="showDiscard" @keep="showDiscard = false" @discard="leave()" />
        <confirm-delete v-if="showAIConfirm" @confirm="submit()" @cancel="() => { showAIConfirm = false; }" :msg="AIConfirmText"/>
        <ai-improve-panel v-if="showAIModal" node-type="checklist" :slug="node.Slug" :current-draft="{ name: node.Name, items: checklistJSON.items }" @close="showAIModal = false" @apply="d => { onAIDraft(d); showAIModal = true }" @accept="onAIDraft" />
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import TipTap from '@/components/TipTap.vue';
import TipTapToolbar from '@/components/TipTapToolbar.vue';
import AIImprovePanel from '@/components/AIImprovePanel.vue';
import ConfirmDelete from '@/components/ConfirmDelete.vue';
import EditorBar from '@/components/EditorBar.vue';
import EditorDocHead from '@/components/EditorDocHead.vue';
import EditorActivity from '@/components/EditorActivity.vue';
import EditorDetailsDrawer from '@/components/EditorDetailsDrawer.vue';
import EditorSaveDialog from '@/components/EditorSaveDialog.vue';
import EditorDiscardDialog from '@/components/EditorDiscardDialog.vue';
import nodeEditor from '@/nodeEditor';

// ChecklistJSON: { items: daily tasks/notes, weeklyItems: weekly tasks/notes }
//   task: { id, label, name (unused, ''), description, section | weekday }
//   note: { id, type: 'textblock', text (html), section | weekday }
// Daily items are grouped by time of day ("section", '' = Any time) and weekly items by weekday
// (0 = Sunday … 6 = Saturday; a weekly note with no weekday shows every day). Lists are saved in
// group order, which is the order the live checklist shows them.
const TIMES = [ 'Begin shift', 'Morning', 'Mid day', 'Mid shift', 'Afternoon', 'Evening', 'End of shift', 'Overnight' ];
const DAILY_GROUPS = [ { key: '', label: 'Any time' }, ...TIMES.map(t => ({ key: t, label: t })) ];
const NO_DAY = 'none';
const WEEKLY_GROUPS = [ { key: NO_DAY, label: 'Every day (notes)' }, ...[ 1, 2, 3, 4, 5, 6, 0 ].map(d => ({ key: String(d), label: [ 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday' ][d] })) ];

// Marks clipboard JSON as a copied checklist item so Paste ignores anything else
const CLIPBOARD_TYPE = 'playbook-checklist-item';

// What counts as a change (the group is added by the editor, the same way it groups)
function normalizeItem(item: any) {
    return item.type == 'textblock'
        ? { id: item.id, type: 'textblock', text: item.text || '' }
        : { id: item.id, label: item.label || '', description: item.description || '' };
}

export default defineComponent({
    name: 'EditChecklist',
    mixins: [ nodeEditor ],
    components: {
        TipTap,
        TipTapToolbar,
        AiImprovePanel: AIImprovePanel,
        ConfirmDelete,
        EditorBar,
        EditorDocHead,
        EditorActivity,
        EditorDetailsDrawer,
        EditorSaveDialog,
        EditorDiscardDialog
    },
    data() {
        return {
            checklistJSON: { items: [], weeklyItems: [] } as any,
            NO_DAY,
            lists: [
                { key: 'items', title: 'Every day', hint: 'Grouped by time of day, in the order people will see them.', empty: 'No daily tasks yet.' },
                { key: 'weeklyItems', title: 'Weekly', hint: 'These show up on the checklist only on their day.', empty: 'No weekly tasks yet.' }
            ],
            activeEditor: null as any,
            copiedID: null as any,
            showAIModal: false
        }
    },
    beforeRouteLeave(to: any, from: any, next: any) {
        this.guardLeave(next);
    },
    methods: {
        // nodeEditor content hooks
        contentField(): string {
            return 'ChecklistJSON';
        },
        loadContent(json: any) {
            this.checklistJSON = json || {};
            if(!Array.isArray(this.checklistJSON.items)) this.checklistJSON.items = [];
            if(!Array.isArray(this.checklistJSON.weeklyItems)) this.checklistJSON.weeklyItems = [];
            for(const key of [ 'items', 'weeklyItems' ]) {
                for(const item of this.checklistJSON[key]) if(!item.id) item.id = uuidv4();
            }
        },
        // Saved in group order (time of day / weekday), the order the live checklist shows
        contentData(): any {
            return {
                ...this.checklistJSON,
                items: this.ordered('items'),
                weeklyItems: this.ordered('weeklyItems')
            };
        },
        contentSnapshot(): any {
            const snap = (listKey: string) => this.ordered(listKey).map((item: any) => ({ ...normalizeItem(item), group: this.groupKeyOf(listKey, item) }));
            return { items: snap('items'), weeklyItems: snap('weeklyItems') };
        },
        typeTitle(): string {
            return 'Checklist';
        },

        groupDefs(listKey: string): any[] {
            return listKey == 'items' ? DAILY_GROUPS : WEEKLY_GROUPS;
        },
        // Which group an item is in: its time of day, or its weekday as a string (NO_DAY for a note without one)
        groupKeyOf(listKey: string, item: any): string {
            if(listKey == 'items') return TIMES.includes(item.section) ? item.section : '';
            return item.weekday == null || item.weekday === '' ? NO_DAY : String(item.weekday);
        },
        ordered(listKey: string): any[] {
            const list = this.checklistJSON[listKey] || [];
            return this.groupDefs(listKey).flatMap(g => list.filter((item: any) => this.groupKeyOf(listKey, item) === g.key));
        },
        groupsOf(listKey: string): any[] {
            const list = this.checklistJSON[listKey] || [];
            return this.groupDefs(listKey)
                .map(g => ({ ...g, rows: list.map((item: any, index: number) => ({ item, index })).filter((r: any) => this.groupKeyOf(listKey, r.item) === g.key) }))
                .filter(g => g.rows.length);
        },
        unusedGroups(listKey: string): any[] {
            const used = this.groupsOf(listKey).map(g => g.key);
            return this.groupDefs(listKey).filter(g => g.key !== NO_DAY && !used.includes(g.key));
        },
        // Tasks can't go to "Every day" (weekly notes only)
        moveOptions(listKey: string, item: any): any[] {
            return this.groupDefs(listKey).filter(g => g.key !== NO_DAY || item.type == 'textblock');
        },
        taskCount(listKey: string): number {
            return (this.checklistJSON[listKey] || []).filter((item: any) => item.type != 'textblock').length;
        },
        setGroup(listKey: string, item: any, groupKey: string) {
            if(listKey == 'items') {
                item.section = groupKey;
            } else if(groupKey === NO_DAY) {
                delete item.weekday;
            } else {
                item.weekday = parseInt(groupKey);
            }
        },
        // New items go to the end of their group
        insertInGroup(listKey: string, groupKey: string, item: any) {
            this.setGroup(listKey, item, groupKey);
            const list = this.checklistJSON[listKey];
            let last = -1;
            list.forEach((it: any, i: number) => { if(this.groupKeyOf(listKey, it) === groupKey) last = i; });
            if(last > -1) list.splice(last + 1, 0, item);
            else list.push(item);
        },
        addTask(listKey: string, groupKey: string) {
            const item = { id: uuidv4(), label: '', name: '', description: '' };
            this.insertInGroup(listKey, groupKey, item);
            this.$nextTick(() => {
                const input = document.querySelector(`[data-cl="${item.id}"]`) as HTMLInputElement;
                if(input) {
                    input.focus();
                    input.scrollIntoView({ block: 'center' });
                }
            });
        },
        addNote(listKey: string, groupKey: string) {
            this.insertInGroup(listKey, groupKey, { id: uuidv4(), type: 'textblock', text: '' });
        },
        moveWithin(listKey: string, group: any, k: number, dir: number) {
            const other = group.rows[k + dir];
            if(!other) return;
            const list = this.checklistJSON[listKey];
            const a = group.rows[k].index, b = other.index;
            [list[a], list[b]] = [list[b], list[a]];
        },
        moveToGroup(listKey: string, index: number, groupKey: string) {
            const [item] = this.checklistJSON[listKey].splice(index, 1);
            this.insertInGroup(listKey, groupKey, item);
        },
        duplicate(listKey: string, index: number) {
            const list = this.checklistJSON[listKey];
            const copy = { ...JSON.parse(JSON.stringify(list[index])), id: uuidv4() };
            list.splice(index + 1, 0, copy);
        },
        remove(listKey: string, index: number) {
            this.checklistJSON[listKey].splice(index, 1);
            this.activeEditor = null;
        },
        jumpTo(listKey: string, groupKey: string) {
            const el = document.getElementById('cl-group-' + listKey + '-' + groupKey);
            if(el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        },
        // Clicking a toolbar button shouldn't take the cursor out of the note being formatted
        keepEditorFocus(e: any) {
            if(e.target.tagName != 'INPUT' && e.target.tagName != 'SELECT') e.preventDefault();
        },
        // Copies an item to the system clipboard as tagged JSON, so it can be pasted into any checklist
        async copyItem(field: any) {
            const item = field.type == 'textblock'
                ? { type: 'textblock', text: field.text || '', section: field.section, weekday: field.weekday }
                : { type: 'item', label: field.label || '', description: field.description || '', section: field.section, weekday: field.weekday };
            try {
                await navigator.clipboard.writeText(JSON.stringify({ type: CLIPBOARD_TYPE, item: item }, null, 2));
                this.copiedID = field.id;
                setTimeout(() => {
                    if(this.copiedID == field.id) this.copiedID = null;
                }, 1500);
            } catch(e) {
                console.log(e);
                alert('Could not copy to the clipboard');
            }
        },
        // Adds a copied item (from copyItem, in any checklist) to the end of this group
        async pasteItem(listKey: string, groupKey: string) {
            let text = '';
            try {
                text = await navigator.clipboard.readText();
            } catch(e) {
                console.log(e);
                alert('Could not read the clipboard. Allow clipboard access for this site and try again.');
                return;
            }
            let copied: any = null;
            try {
                const parsed = JSON.parse(text);
                if(parsed?.type == CLIPBOARD_TYPE && parsed.item && typeof parsed.item == 'object') copied = parsed.item;
            } catch(e) {
                // not JSON
            }
            const isNote = copied?.type == 'textblock' && typeof copied.text == 'string';
            const isTask = copied?.type == 'item' && typeof copied.label == 'string';
            if(!isNote && !isTask) {
                alert('The clipboard doesn\'t contain a checklist item. Use the copy icon on an item first.');
                return;
            }
            if(isTask && groupKey === NO_DAY) {
                alert('Only notes can go under "Every day". Paste the task into one of the days.');
                return;
            }
            const item: any = isNote
                ? { id: uuidv4(), type: 'textblock', text: copied.text }
                : { id: uuidv4(), label: copied.label, name: '', description: typeof copied.description == 'string' ? copied.description : '' };
            this.insertInGroup(listKey, groupKey, item);
        },
        onAIDraft(draft: any) {
            // Items arrive already in ChecklistJSON shape (ids assigned server-side)
            this.node.Name = draft.name;
            this.activeEditor = null;
            this.checklistJSON.items = draft.items;
            this.showAIModal = false;
        }
    }
});
</script>
